import { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppRadioGroup, AppTextField } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import { GRID_FULL, GRID_HALF } from "../../lib/ui/formLayout";
import type {
  DashboardDecorationPlacement,
  DashboardFieldBinding,
  DashboardTableBinding,
  DashboardTableColumnBinding,
  DataQueryExecutionResult,
} from "../../types/dashboard";
import type { DashboardQuery } from "../../types/query";
import { parseStaticTable } from "../../utils/decorationCatalog";
import { flattenOpenSearchPaths, type OpenSearchPathOption } from "../../utils/flattenOpenSearchPaths";
import {
  bindableFieldsForKind,
  bindingFieldLabelKey,
} from "../../utils/decorationBindingFields";
import {
  formatBoundValue,
  getValueAtPath,
  syncTableBindingColumns,
} from "../../utils/resolveDecorationBindings";
import { arrayResultPathOptions, itemFieldPathOptions } from "../../utils/tableBindingPaths";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const FORMAT_OPTIONS = [
  { value: "", labelKey: "dashboard.binding.formatNone" },
  { value: "number", labelKey: "dashboard.binding.formatNumber" },
  { value: "percent", labelKey: "dashboard.binding.formatPercent" },
  { value: "date", labelKey: "dashboard.binding.formatDate" },
] as const;

type FormatOption = { value: string; labelKey: (typeof FORMAT_OPTIONS)[number]["labelKey"] };
type QueryOption = { id: string; label: string };

type Props = {
  draft: DashboardDecorationPlacement;
  assignedQueries: DashboardQuery[];
  dataQueries?: DataQueryExecutionResult[];
  onChange: (next: DashboardDecorationPlacement) => void;
};

function previewBindingValue(
  binding: DashboardFieldBinding | undefined,
  dataQueries: DataQueryExecutionResult[]
): string {
  if (!binding?.queryId || !binding.valuePath) return "";
  const queryResult = dataQueries.find((q) => q.queryId === binding.queryId);
  if (!queryResult || queryResult.error) return binding.fallback || "";
  const raw = getValueAtPath(queryResult.openSearchResponse, binding.valuePath);
  if (raw === null || raw === undefined || raw === "") return binding.fallback || "";
  return formatBoundValue(raw, binding.format);
}

function previewColumnValue(
  tableBinding: DashboardTableBinding | undefined,
  columnIndex: number,
  dataQueries: DataQueryExecutionResult[]
): string {
  if (!tableBinding?.queryId || !tableBinding.resultPath) return "";
  const column = tableBinding.columns?.[columnIndex];
  if (!column?.fieldPath) return column?.fallback || "";
  const queryResult = dataQueries.find((q) => q.queryId === tableBinding.queryId);
  if (!queryResult || queryResult.error) return column.fallback || "";
  const rows = getValueAtPath(queryResult.openSearchResponse, tableBinding.resultPath);
  if (!Array.isArray(rows) || rows.length === 0) return column.fallback || "";
  const raw = getValueAtPath(rows[0], column.fieldPath);
  if (raw === null || raw === undefined || raw === "") return column.fallback || "";
  return formatBoundValue(raw, column.format) || column.fallback || "";
}

function BindingModeHeaderRow({
  title,
  mode,
  onModeChange,
}: {
  title: string;
  mode: "static" | "dynamic";
  onModeChange: (mode: "static" | "dynamic") => void;
}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationBinding;

  return (
    <Box sx={styles.fieldHeaderRow}>
      <Typography component="div" sx={styles.fieldTitle}>
        {title}
      </Typography>
      <Box sx={styles.fieldHeaderRadioCol}>
        <AppRadioGroup
          label={t("dashboard.binding.tab")}
          value={mode}
          onChange={(value) => onModeChange(value as "static" | "dynamic")}
          options={[
            { value: "static", label: t("dashboard.binding.static") },
            { value: "dynamic", label: t("dashboard.binding.dynamic") },
          ]}
          row
          formControlProps={{ sx: styles.radioFormControl }}
        />
      </Box>
    </Box>
  );
}

function FieldBindingEditor({
  fieldPath,
  draft,
  queryOptions,
  dataQueries,
  onPatchBinding,
  onSetBindingMode,
}: {
  fieldPath: string;
  draft: DashboardDecorationPlacement;
  queryOptions: QueryOption[];
  dataQueries: DataQueryExecutionResult[];
  onPatchBinding: (fieldPath: string, patch: Partial<DashboardFieldBinding> | null) => void;
  onSetBindingMode: (fieldPath: string, mode: "static" | "dynamic") => void;
}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationBinding;
  const binding = draft.bindings?.[fieldPath];
  const mode = binding ? "dynamic" : "static";
  const selectedQuery = queryOptions.find((q) => q.id === binding?.queryId) || null;
  const pathOptions = useMemo(() => {
    if (!binding?.queryId) return [] as OpenSearchPathOption[];
    const result = dataQueries.find((q) => q.queryId === binding.queryId);
    return flattenOpenSearchPaths(result?.openSearchResponse);
  }, [binding?.queryId, dataQueries]);
  const selectedPath = pathOptions.find((option) => option.path === binding?.valuePath) || null;
  const formatOptions = useMemo<FormatOption[]>(
    () => FORMAT_OPTIONS.map((option) => ({ value: option.value, labelKey: option.labelKey })),
    []
  );
  const selectedFormat =
    formatOptions.find((option) => option.value === (binding?.format || "")) || formatOptions[0];
  const preview = previewBindingValue(binding, dataQueries);

  return (
    <Box sx={styles.fieldCard}>
      <BindingModeHeaderRow
        title={t(bindingFieldLabelKey(fieldPath))}
        mode={mode}
        onModeChange={(nextMode) => onSetBindingMode(fieldPath, nextMode)}
      />

      {mode === "dynamic" && binding && (
        <AppFormGrid spacing="compact" sx={styles.fieldGrid}>
          <AppFormField size={GRID_HALF}>
            <AppAutocomplete<QueryOption>
              label={t("dashboard.binding.query")}
              options={queryOptions}
              value={selectedQuery}
              onChange={(_, option) => {
                if (!option) return;
                onPatchBinding(fieldPath, { queryId: option.id, valuePath: "" });
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disableClearable
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppAutocomplete<OpenSearchPathOption>
              label={t("dashboard.binding.path")}
              options={pathOptions}
              value={selectedPath}
              onChange={(_, option) => {
                onPatchBinding(fieldPath, { valuePath: option?.path || "" });
              }}
              getOptionLabel={(option) => option.path}
              isOptionEqualToValue={(a, b) => a.path === b.path}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.path}>
                  <Box sx={styles.pathOptionLabel}>
                    <Typography variant="body2">{option.path}</Typography>
                    <Typography variant="caption" sx={styles.pathSample}>
                      {option.sampleValue}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppAutocomplete<FormatOption>
              label={t("dashboard.binding.format")}
              options={formatOptions}
              value={selectedFormat}
              onChange={(_, option) => {
                if (!option) return;
                onPatchBinding(fieldPath, {
                  format: option.value || undefined,
                });
              }}
              getOptionLabel={(option) => t(option.labelKey)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              disableClearable
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppTextField
              label={t("dashboard.binding.fallback")}
              value={binding.fallback || ""}
              onChange={(e) => onPatchBinding(fieldPath, { fallback: e.target.value || undefined })}
              helperText={t("dashboard.binding.fallbackHint")}
            />
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <Box sx={styles.previewSection}>
              <Typography component="span" sx={styles.previewLabel}>
                {t("dashboard.binding.preview")}
              </Typography>
              <Box component="span" sx={styles.previewValue}>
                {preview || "—"}
              </Box>
            </Box>
          </AppFormField>
        </AppFormGrid>
      )}
    </Box>
  );
}

function TableColumnBindingEditor({
  header,
  columnIndex,
  column,
  fieldOptions,
  preview,
  onPatchColumn,
}: {
  header: string;
  columnIndex: number;
  column: DashboardTableColumnBinding;
  fieldOptions: OpenSearchPathOption[];
  preview: string;
  onPatchColumn: (columnIndex: number, patch: Partial<DashboardTableColumnBinding>) => void;
}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationBinding;
  const formatOptions = useMemo<FormatOption[]>(
    () => FORMAT_OPTIONS.map((option) => ({ value: option.value, labelKey: option.labelKey })),
    []
  );
  const selectedField = fieldOptions.find((option) => option.path === column.fieldPath) || null;
  const selectedFormat =
    formatOptions.find((option) => option.value === (column.format || "")) || formatOptions[0];

  return (
    <Box sx={styles.columnCard}>
      <Typography component="div" sx={styles.columnTitle}>
        {t("dashboard.binding.tableColumn")} {columnIndex + 1}: {header || "—"}
      </Typography>
      <AppFormGrid spacing="compact">
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<OpenSearchPathOption>
            label={t("dashboard.binding.tableColumnField")}
            options={fieldOptions}
            value={selectedField}
            onChange={(_, option) => {
              onPatchColumn(columnIndex, { fieldPath: option?.path || "" });
            }}
            getOptionLabel={(option) => option.path}
            isOptionEqualToValue={(a, b) => a.path === b.path}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.path}>
                <Box sx={styles.pathOptionLabel}>
                  <Typography variant="body2">{option.path}</Typography>
                  <Typography variant="caption" sx={styles.pathSample}>
                    {option.sampleValue}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<FormatOption>
            label={t("dashboard.binding.format")}
            options={formatOptions}
            value={selectedFormat}
            onChange={(_, option) => {
              if (!option) return;
              onPatchColumn(columnIndex, { format: option.value || undefined });
            }}
            getOptionLabel={(option) => t(option.labelKey)}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppTextField
            label={t("dashboard.binding.fallback")}
            value={column.fallback || ""}
            onChange={(e) => onPatchColumn(columnIndex, { fallback: e.target.value || undefined })}
            helperText={t("dashboard.binding.fallbackHint")}
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <Box sx={styles.previewSection}>
            <Typography component="span" sx={styles.previewLabel}>
              {t("dashboard.binding.tableColumnPreview")}
            </Typography>
            <Box component="span" sx={styles.previewValue}>
              {preview || "—"}
            </Box>
          </Box>
        </AppFormField>
      </AppFormGrid>
    </Box>
  );
}

function TableBindingEditor({
  draft,
  queryOptions,
  dataQueries,
  onChange,
}: {
  draft: DashboardDecorationPlacement;
  queryOptions: QueryOption[];
  dataQueries: DataQueryExecutionResult[];
  onChange: (next: DashboardDecorationPlacement) => void;
}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationBinding;
  const headers = useMemo(() => parseStaticTable(draft.content).headers, [draft.content]);
  const tableBinding = useMemo(() => syncTableBindingColumns(draft), [draft]);
  const mode = tableBinding ? "dynamic" : "static";
  const selectedQuery =
    queryOptions.find((q) => q.id === tableBinding?.queryId) || null;
  const queryResult = dataQueries.find((q) => q.queryId === tableBinding?.queryId);
  const resultPathOptions = useMemo(
    () => arrayResultPathOptions(queryResult?.openSearchResponse),
    [queryResult?.openSearchResponse]
  );
  const selectedResultPath =
    resultPathOptions.find((option) => option.path === tableBinding?.resultPath) || null;
  const columnFieldOptions = useMemo(() => {
    if (!tableBinding?.resultPath) return [] as OpenSearchPathOption[];
    return itemFieldPathOptions(queryResult?.openSearchResponse, tableBinding.resultPath);
  }, [queryResult?.openSearchResponse, tableBinding?.resultPath]);

  const patchTableBinding = (patch: Partial<DashboardTableBinding> | null) => {
    if (!patch) {
      onChange({ ...draft, tableBinding: undefined });
      return;
    }
    const nextBinding = syncTableBindingColumns({
      ...draft,
      tableBinding: { ...tableBinding, ...patch } as DashboardTableBinding,
    });
    onChange({ ...draft, tableBinding: nextBinding });
  };

  const setTableMode = (nextMode: "static" | "dynamic") => {
    if (nextMode === "static") {
      onChange({ ...draft, tableBinding: undefined });
      return;
    }
    const firstQueryId = queryOptions[0]?.id || "";
    patchTableBinding({
      queryId: tableBinding?.queryId || firstQueryId,
      resultPath: tableBinding?.resultPath || "",
      columns: headers.map((_, index) => tableBinding?.columns?.[index] ?? { fieldPath: "" }),
    });
  };

  const patchColumn = (columnIndex: number, patch: Partial<DashboardTableColumnBinding>) => {
    if (!tableBinding) return;
    const columns = [...(tableBinding.columns ?? [])];
    columns[columnIndex] = { ...columns[columnIndex], ...patch };
    patchTableBinding({ columns });
  };

  return (
    <Box sx={styles.tableSection}>
      <BindingModeHeaderRow
        title={t("dashboard.binding.tableData")}
        mode={mode}
        onModeChange={setTableMode}
      />

      {mode === "dynamic" && tableBinding && (
        <>
          <AppFormGrid spacing="compact">
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<QueryOption>
                label={t("dashboard.binding.query")}
                options={queryOptions}
                value={selectedQuery}
                onChange={(_, option) => {
                  if (!option) return;
                  patchTableBinding({ queryId: option.id, resultPath: "" });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disableClearable
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<OpenSearchPathOption>
                label={t("dashboard.binding.tableResultPath")}
                options={resultPathOptions}
                value={selectedResultPath}
                onChange={(_, option) => {
                  patchTableBinding({ resultPath: option?.path || "" });
                }}
                getOptionLabel={(option) => option.path}
                isOptionEqualToValue={(a, b) => a.path === b.path}
                helperText={t("dashboard.binding.tableResultPathHint")}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.path}>
                    <Box sx={styles.pathOptionLabel}>
                      <Typography variant="body2">{option.path}</Typography>
                      <Typography variant="caption" sx={styles.pathSample}>
                        {option.sampleValue}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </AppFormField>
          </AppFormGrid>

          {resultPathOptions.length === 0 && tableBinding.queryId && (
            <Alert severity="info" sx={styles.emptyAlert}>
              {t("dashboard.binding.tableNoArrayPaths")}
            </Alert>
          )}

          {headers.length === 0 ? (
            <Alert severity="info" sx={styles.emptyAlert}>
              {t("dashboard.binding.tableNoColumns")}
            </Alert>
          ) : (
            tableBinding.columns?.map((column, columnIndex) => (
              <TableColumnBindingEditor
                key={`${headers[columnIndex]}-${columnIndex}`}
                header={headers[columnIndex]}
                columnIndex={columnIndex}
                column={column}
                fieldOptions={columnFieldOptions}
                preview={previewColumnValue(tableBinding, columnIndex, dataQueries)}
                onPatchColumn={patchColumn}
              />
            ))
          )}
        </>
      )}
    </Box>
  );
}

export default function DecorationBindingPanel({
  draft,
  assignedQueries,
  dataQueries = [],
  onChange,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationBinding;
  const fields = bindableFieldsForKind(draft.kind);

  const queryOptions = useMemo(
    () => assignedQueries.filter((q) => q.id).map((q) => ({ id: q.id!, label: q.name || q.id! })),
    [assignedQueries]
  );

  const patchBinding = (fieldPath: string, patch: Partial<DashboardFieldBinding> | null) => {
    const nextBindings = { ...(draft.bindings || {}) };
    if (!patch) {
      delete nextBindings[fieldPath];
    } else {
      nextBindings[fieldPath] = { ...nextBindings[fieldPath], ...patch } as DashboardFieldBinding;
    }
    onChange({ ...draft, bindings: Object.keys(nextBindings).length ? nextBindings : undefined });
  };

  const setBindingMode = (fieldPath: string, mode: "static" | "dynamic") => {
    if (mode === "static") {
      patchBinding(fieldPath, null);
      return;
    }
    const firstQueryId = queryOptions[0]?.id || "";
    patchBinding(fieldPath, {
      queryId: draft.bindings?.[fieldPath]?.queryId || firstQueryId,
      valuePath: draft.bindings?.[fieldPath]?.valuePath || "",
      format: draft.bindings?.[fieldPath]?.format,
      fallback: draft.bindings?.[fieldPath]?.fallback,
    });
  };

  const showFieldBindings = fields.length > 0;
  const showTableBinding = draft.kind === "table";

  if (!showFieldBindings && !showTableBinding) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("dashboard.decor.dividerHint")}
      </Typography>
    );
  }

  if (assignedQueries.length === 0) {
    return (
      <Alert severity="info" sx={styles.emptyAlert}>
        {t("dashboard.binding.noQueriesAssigned")}
      </Alert>
    );
  }

  return (
    <Box sx={styles.root}>
      {fields.map((fieldPath) => (
        <FieldBindingEditor
          key={fieldPath}
          fieldPath={fieldPath}
          draft={draft}
          queryOptions={queryOptions}
          dataQueries={dataQueries}
          onPatchBinding={patchBinding}
          onSetBindingMode={setBindingMode}
        />
      ))}
      {showTableBinding && (
        <TableBindingEditor
          draft={draft}
          queryOptions={queryOptions}
          dataQueries={dataQueries}
          onChange={onChange}
        />
      )}
    </Box>
  );
}
